import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../components/api';

export const useBulletinData = () => {
  const [searchParams] = useSearchParams();
  
  // États locaux
  const [bulletins, setBulletins] = useState([]);
  const [classe, setClasse] = useState(null);
  const [statistiques, setStatistiques] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États des filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('tous');
  const [semestreFilter, setSemestreFilter] = useState('');
  const [anneeFilter, setAnneeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // États des données de référence
  const [semestres, setSemestres] = useState([]);
  const [annees, setAnnees] = useState([]);
  
  // Récupérer les paramètres de l'URL
  const classeId = searchParams.get('classe_id');
  const initialSemestre = searchParams.get('semestre_id');
  const initialAnnee = searchParams.get('annee_academique_id');

  const fetchReferenceData = async () => {
    try {
      // Récupérer les semestres
      const semestresResponse = await api.get('/emplois/semestres/actifs/');
      setSemestres(semestresResponse.data || []);
      
      // Récupérer les années académiques
      const anneesResponse = await api.get('/etablissement/annees-academiques/');
      setAnnees(anneesResponse.data || []);
      
      // Initialiser les filtres avec les valeurs de l'URL
      if (initialSemestre) setSemestreFilter(initialSemestre);
      if (initialAnnee) setAnneeFilter(initialAnnee);
      
    } catch (err) {
      console.error('Erreur lors du chargement des données de référence:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Construire les paramètres de requête
      const params = new URLSearchParams({
        classe_id: classeId,
        page: '1',
        page_size: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'tous') params.append('status', statusFilter);
      if (semestreFilter) params.append('semestre_id', semestreFilter);
      if (anneeFilter) params.append('annee_academique_id', anneeFilter);
      
      const response = await api.get(`/note/bulletins-classe?${params.toString()}`);
      
      if (response.data.success) {
        const { bulletins, classe: classeData, statistiques: statsData, pagination: paginationData } = response.data.data;
        
        setBulletins(bulletins || []);
        setClasse(classeData);
        setStatistiques(statsData || {});
        setPagination(paginationData || {});
      } else {
        setError(response.data.error || 'Erreur lors du chargement des données');
      }
      
    } catch (err) {
      console.error('Erreur fetchData:', err);
      
      let errorMessage = 'Erreur lors du chargement des bulletins';
      
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = 'API non trouvée. Vérifiez que l\'URL existe dans l\'application concernée.';
        } else if (err.response.status === 403) {
          errorMessage = 'Accès refusé. Vérifiez vos permissions.';
        } else if (err.response.status >= 500) {
          errorMessage = 'Erreur serveur. Contactez l\'administrateur.';
        } else {
          errorMessage = `Erreur ${err.response.status}: ${err.response.data?.error || err.response.statusText}`;
        }
      } else if (err.request) {
        errorMessage = 'Pas de réponse du serveur. Vérifiez que le backend est démarré.';
      } else {
        errorMessage = `Erreur: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = async (page) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        classe_id: classeId,
        page: page.toString(),
        page_size: '20'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'tous') params.append('status', statusFilter);
      if (semestreFilter) params.append('semestre_id', semestreFilter);
      if (anneeFilter) params.append('annee_academique_id', anneeFilter);
      
      const response = await api.get(`/note/bulletins-classe?${params.toString()}`);
      
      if (response.data.success) {
        const { bulletins, pagination: paginationData } = response.data.data;
        setBulletins(bulletins || []);
        setPagination(paginationData || {});
      }
      
    } catch (err) {
      console.error('Erreur lors du changement de page:', err);
      setError('Erreur lors du changement de page');
    } finally {
      setLoading(false);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    console.log('Changement de taille de page:', newPageSize);
    // TODO: Implémenter le changement de taille de page
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('tous');
    setSemestreFilter('');
    setAnneeFilter('');
    setShowFilters(false);
  };

  useEffect(() => {
    if (classeId) {
      fetchData();
      fetchReferenceData();
    }
  }, [classeId]);

  useEffect(() => {
    if (classeId) {
      fetchData();
    }
  }, [searchTerm, statusFilter, semestreFilter, anneeFilter]);

  return {
    // États
    bulletins,
    classe,
    statistiques,
    pagination,
    loading,
    error,
    searchTerm,
    statusFilter,
    semestreFilter,
    anneeFilter,
    showFilters,
    semestres,
    annees,
    classeId,
    
    // Setters
    setSearchTerm,
    setStatusFilter,
    setSemestreFilter,
    setAnneeFilter,
    setShowFilters,
    
    // Actions
    fetchData,
    handlePageChange,
    handlePageSizeChange,
    clearFilters
  };
};